import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cache blob URLs per athlete to avoid re-fetching
const blobCache = {};

export const AthleteAvatar = ({ athlete, size = 'md', className = '', style = {} }) => {
  const [blobUrl, setBlobUrl] = useState(null);

  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-12 w-12',
    lg: 'h-16 w-16',
  };

  useEffect(() => {
    if (!athlete?.photo_path) {
      setBlobUrl(null);
      return;
    }

    const cacheKey = `${athlete.id}-${athlete.photo_path}`;

    // Use cached blob if available
    if (blobCache[cacheKey]) {
      setBlobUrl(blobCache[cacheKey]);
      return;
    }

    let cancelled = false;

    const fetchPhoto = async () => {
      try {
        const response = await axios.get(`${API}/upload/headshot/${athlete.id}`, {
          responseType: 'blob',
          withCredentials: true,
        });
        if (!cancelled) {
          const url = URL.createObjectURL(response.data);
          blobCache[cacheKey] = url;
          setBlobUrl(url);
        }
      } catch {
        // No photo or failed to load - silently use fallback
      }
    };

    fetchPhoto();

    return () => {
      cancelled = true;
    };
  }, [athlete?.id, athlete?.photo_path]);

  const sizeClass = sizeClasses[size] || sizeClasses.md;
  const defaultBg = style.backgroundColor || '#0067B9';

  // Generate fallback initials from name if avatar is empty
  const getInitials = () => {
    if (athlete?.avatar) return athlete.avatar;
    if (athlete?.name) {
      const parts = athlete.name.split(/\s+/);
      return parts.map(p => p[0]?.toUpperCase() || '').join('');
    }
    return '??';
  };

  return (
    <Avatar className={`${sizeClass} ${className}`} style={{ backgroundColor: defaultBg, ...style }} data-testid={`athlete-avatar-${athlete?.id}`}>
      {blobUrl && (
        <AvatarImage src={blobUrl} alt={athlete?.name || ''} className="object-cover" />
      )}
      <AvatarFallback className="text-white font-semibold" style={{ backgroundColor: defaultBg }}>
        {getInitials()}
      </AvatarFallback>
    </Avatar>
  );
};

// Clear cache for a specific athlete (call after upload/delete)
export const clearAvatarCache = (athleteId) => {
  Object.keys(blobCache).forEach(key => {
    if (key.startsWith(`${athleteId}-`)) {
      URL.revokeObjectURL(blobCache[key]);
      delete blobCache[key];
    }
  });
};

export default AthleteAvatar;
